import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, Loader2 } from "lucide-react";
import api, { formatMoney } from "../lib/api";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

const pct = (v) => (v == null ? "—" : `${String(v).replace(".", ",")} %`);
const rot = (v) => (v == null ? "—" : `${String(v).replace(".", ",")}×`);

export function RankingMargenRotacion({ segment }) {
  const [data, setData] = useState(null);
  const [todos, setTodos] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    setData(null);
    api.get("/lab-analisis/ranking", { params: { segment } }).then((r) => setData(r.data)).catch(() => setData({ labs: [] }));
  }, [segment]);
  const sort = useSortable(data?.labs || [], "rotacion", "asc");
  if (!data) return <div className="card p-5 mb-6 text-slate-500 flex gap-2" data-testid="ranking-mr-cargando"><Loader2 size={16} className="animate-spin" /> Cargando ranking…</div>;
  const rows = todos ? sort.sortedRows : sort.sortedRows.slice(0, 15);
  const seg = segment === "farmacia" ? "Farmacia" : "Parafarmacia";
  return (
    <div className="card p-5 mb-6" data-testid="ranking-margen-rotacion">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Gauge size={12} /> Para negociar · {seg}</div>
          <h3 className="font-display text-xl font-bold">Margen y rotación por laboratorio</h3>
          <p className="text-xs text-slate-500 mt-1">Laboratorios que venden más de {formatMoney(data.minimo)} al año, peor rotación primero. Rotación = ventas a PVP ÷ stock a PVP.
            Media de {seg.toLowerCase()}: margen <b>{pct(data.media_margen_pct)}</b> · rotación <b>{rot(data.media_rotacion)}</b>. En rojo, los que están por debajo de la media.</p>
        </div>
        <ExportButtons filename={`margen-rotacion-${segment}`} title={`Margen y rotación por laboratorio · ${seg}`} sheetName={seg} testIdPrefix="ranking-mr-export"
          rows={sort.sortedRows}
          columns={[{ header: "Laboratorio", key: "laboratorio" }, { header: "Ventas anuales (€)", key: "ventas_anual", format: (v) => (v || 0).toFixed(2), align: "right" },
            { header: "Stock PVP (€)", key: "stock_pvp", format: (v) => (v || 0).toFixed(2), align: "right" }, { header: "Margen %", key: "margen_pct", align: "right" },
            { header: "Margen (€)", key: "margen_eur", format: (v) => (v || 0).toFixed(2), align: "right" }, { header: "Rotación", key: "rotacion", align: "right" },
            { header: "Parado D coste (€)", key: "d_coste", format: (v) => (v || 0).toFixed(2), align: "right" }]} />
      </div>
      <div className="overflow-x-auto">
        <table className="dense w-full" data-testid="ranking-mr-tabla">
          <thead><tr><th>#</th><th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
            <th className="num"><SortHeader label="Ventas anuales" k="ventas_anual" sort={sort} num /></th>
            <th className="num"><SortHeader label="Stock PVP" k="stock_pvp" sort={sort} num /></th>
            <th className="num"><SortHeader label="Margen %" k="margen_pct" sort={sort} num /></th>
            <th className="num"><SortHeader label="Margen €" k="margen_eur" sort={sort} num /></th>
            <th className="num"><SortHeader label="Rotación" k="rotacion" sort={sort} num /></th>
            <th className="num"><SortHeader label="Parado D" k="d_coste" sort={sort} num /></th></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={8} className="text-center py-6 text-slate-500">Ningún laboratorio supera el mínimo de ventas.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.laboratorio} className="cursor-pointer hover:bg-slate-50" data-testid={`ranking-mr-fila-${i}`}
                  onClick={() => navigate(`/labs/laboratorio?lab=${encodeURIComponent(r.laboratorio)}&segment=${segment}`)}>
                <td className="font-mono text-slate-400">{i + 1}</td><td className="font-semibold">{r.laboratorio}</td>
                <td className="num">{formatMoney(r.ventas_anual)}</td><td className="num">{formatMoney(r.stock_pvp)}</td>
                <td className={"num font-semibold " + (r.margen_pct != null && r.margen_pct < data.media_margen_pct ? "text-red-600" : "text-emerald-700")}>{pct(r.margen_pct)}</td>
                <td className="num text-slate-500">{formatMoney(r.margen_eur)}</td>
                <td className={"num font-bold " + (r.rotacion != null && r.rotacion < data.media_rotacion ? "text-red-600" : "text-emerald-700")}>{rot(r.rotacion)}</td>
                <td className="num text-red-600">{formatMoney(r.d_coste)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sort.sortedRows.length > 15 && (
        <button className="btn btn-outline btn-sm mt-3" onClick={() => setTodos(!todos)} data-testid="ranking-mr-ver-todos">
          {todos ? "Ver solo los 15 primeros" : `Ver los ${sort.sortedRows.length} laboratorios`}
        </button>
      )}
    </div>
  );
}
